import {
  Action,
  BaseFlagProps,
  Card,
  openConfirmDialog,
  showNotification,
} from 'platform/components';
import {Text} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {
  CarisServiceRequestState,
  useCancelCarisServiceRequestMutation,
  VehicleCarisServiceRequest,
} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {EMPTY_PLACEHOLDER, handleApiError, refreshDmsDatagrid} from '@dms/shared';

import {buildArray, parseDate, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {CARIS_SERVICE_REQUEST_HISTORY_GRID_CODE} from '../constants/CARIS_SERVICE_REQUEST_HISTORY_GRID_CODE';

const getStateFlag = (state: CarisServiceRequestState): BaseFlagProps =>
  match<CarisServiceRequestState, BaseFlagProps>(state)
    .with('NEW', () => ({label: i18n.t('general.labels.new'), colorScheme: 'blue'}))
    .with('ACCEPTED', () => ({
      label: i18n.t('entity.orderIntegration.labels.accepted'),
      colorScheme: 'green',
    }))
    .with('COMPLETED', () => ({
      label: i18n.t('general.labels.completed'),
      colorScheme: 'neutral',
    }))
    .with('CANCELLED', () => ({
      label: i18n.t('general.labels.cancelled'),
      colorScheme: 'red',
    }))
    .exhaustive();

interface CarisServiceRequestCardProps extends RequiredTestIdProps {
  serviceRequest: VehicleCarisServiceRequest;
  vehicleId: string;
  canCancel: boolean;
}

export function CarisServiceRequestCard(props: CarisServiceRequestCardProps) {
  const formatDateTime = useDateTimeFormatter();
  const [cancelCarisServiceRequest] = useCancelCarisServiceRequestMutation();

  const isNew = props.serviceRequest.state === 'NEW';

  const handleCancel = () =>
    openConfirmDialog({
      text: i18n.t('entity.carisServiceRequest.labels.cancelConfirmation'),
      onConfirm: () =>
        cancelCarisServiceRequest({
          serviceRequestId: props.serviceRequest.id,
          vehicleId: props.vehicleId,
        })
          .unwrap()
          .then(() => {
            showNotification.success(i18n.t('general.notifications.successfullyCancelled'));
            refreshDmsDatagrid(CARIS_SERVICE_REQUEST_HISTORY_GRID_CODE);
          })
          .catch(handleApiError),
      'data-testid': suffixTestId('cancelConfirmDialog', props),
    });

  return (
    <Card
      variant="inlineGrey"
      title={props.serviceRequest.createdBy.name ?? EMPTY_PLACEHOLDER}
      parameters={[formatDateTime('dateTimeShort', parseDate(props.serviceRequest.createdAt))]}
      flags={[getStateFlag(props.serviceRequest.state)]}
      actions={buildArray<Action>().when(isNew && props.canCancel, {
        type: 'button',
        variant: 'secondary',
        title: i18n.t('general.actions.cancel'),
        onClick: handleCancel,
        'data-testid': suffixTestId('cancel', props),
      })}
      data-testid={props['data-testid']}
    >
      <Text size="small" preWrap>
        {props.serviceRequest.description}
      </Text>
    </Card>
  );
}
